import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  ArrowRight,
  UserRound,
  Briefcase,
  ChevronRight,
  Video,
  MessagesSquare,
} from "lucide-react";
import PageSeo from "../components/PageSeo";
import YoutubeVideoLightbox from "../components/YoutubeVideoLightbox";
import { JOBUWIN_INTRO_VIDEO_ID } from "../lib/youtubeVideos";
import Nd1SiteNav from "./components/Nd1SiteNav";
import Nd1SiteFooter from "./components/Nd1SiteFooter";
import Nd1InsightCard from "./components/Nd1InsightCard";
import { getNd1Insights } from "./nd1Insights";
import "./nd1.css";

/* Custom hiring-today row icons (inherit row color via currentColor). */
function ApplicationsIcon(props) {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M20 12H37L46 21V52H20V12Z"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path d="M37 12V21H46" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <line x1="27" y1="33" x2="39" y2="33" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="27" y1="41" x2="39" y2="41" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function AiGeneratedIcon(props) {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect x="18" y="20" width="28" height="22" rx="6" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="26" cy="31" r="2.5" fill="currentColor" />
      <circle cx="38" cy="31" r="2.5" fill="currentColor" />
      <line x1="32" y1="14" x2="32" y2="20" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="32" cy="12" r="2.5" fill="currentColor" />
      <line x1="18" y1="30" x2="12" y2="30" stroke="currentColor" strokeWidth="2.5" />
      <line x1="52" y1="30" x2="46" y2="30" stroke="currentColor" strokeWidth="2.5" />
    </svg>
  );
}

function IrrelevantIcon(props) {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <circle cx="32" cy="32" r="18" stroke="currentColor" strokeWidth="2.5" />
      <line x1="25" y1="25" x2="39" y2="39" stroke="currentColor" strokeWidth="2.5" />
      <line x1="39" y1="25" x2="25" y2="39" stroke="currentColor" strokeWidth="2.5" />
    </svg>
  );
}

function MisleadingIcon(props) {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M32 12L52 48H12L32 12Z" stroke="#c0392b" strokeWidth="2.5" />
      <line x1="32" y1="24" x2="32" y2="36" stroke="#c0392b" strokeWidth="2.5" />
      <circle cx="32" cy="42" r="2" fill="#c0392b" />
    </svg>
  );
}

function TrustShieldIcon(props) {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <circle cx="32" cy="32" r="28" fill="#46591f" />
      <path
        d="M32 13.5L47.5 19V30.5C47.5 41.2 41 49.5 32 53.5C23 49.5 16.5 41.2 16.5 30.5V19L32 13.5Z"
        fill="#6d8f3a"
        stroke="#b8d45a"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path
        d="M32 13.5L47.5 19V30.5C47.5 41.2 41 49.5 32 53.5V13.5Z"
        fill="#556b2f"
        opacity="0.45"
      />
      <path
        d="M24.5 30.5L29.5 35.5L40 24.5"
        stroke="#ffffff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GreenTickIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <circle cx="12" cy="12" r="10" fill="#556B2F" />
      <path
        d="M8 12.2L10.8 15L16 9"
        stroke="#FFFFFF"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FunnelArrowIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M12 5V18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path
        d="M7 13L12 18L17 13"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WatchVideoIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <circle cx="12" cy="12" r="10.25" stroke="#2a3140" strokeWidth="1.5" />
      <path d="M10 8.2L15.6 12L10 15.8V8.2Z" fill="#1f2937" />
    </svg>
  );
}

function GenuineIcon(props) {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <circle cx="32" cy="22" r="8" stroke="#556B2F" strokeWidth="2.5" />
      <path d="M18 48C18 39 24 34 32 34C40 34 46 39 46 48" stroke="#556B2F" strokeWidth="2.5" />
    </svg>
  );
}

const FUNNEL_ROWS = [
  { icon: ApplicationsIcon, num: "500", label: "Applications" },
  { icon: AiGeneratedIcon, num: "350", label: "AI generated", variant: "risk-1" },
  { icon: IrrelevantIcon, num: "100", label: "Irrelevant", variant: "risk-2" },
  { icon: MisleadingIcon, num: "45", label: "Misleading", variant: "warn" },
  { icon: GenuineIcon, num: "5", label: "Genuine", variant: "genuine" },
];

const WITH_JOBUWIN = [
  "Verified employers",
  "Genuine applicant signals",
  "Video introductions",
  "Better conversations",
  "Stronger hires",
];

const STEPS = [
  {
    icon: ShieldCheck,
    title: "Verified employers",
    desc: "Every company is reviewed before jobs are published.",
  },
  {
    icon: Video,
    title: "Genuine applicant signals",
    desc: "Applicants introduce themselves with video and prove who they are.",
  },
  {
    icon: MessagesSquare,
    title: "Better conversations",
    desc: "The right people connect, faster and with more context.",
  },
];

const INSIGHTS = getNd1Insights();

const TRUST_POINTS = [
  "Genuine jobs only",
  "Responsible behaviour by design",
  "Built around trust and authenticity",
];

export default function Nd1HomePage() {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="nd1-root">
      <PageSeo
        title="Genuine jobs. Real people."
        description="JOBUWIN helps genuine applicants and genuine hiring managers find each other through more authentic communication — beyond fake profiles and AI noise."
        path="/"
        canonicalPath="/"
        schemaType="website"
      />

      <a href="#nd1-main" className="nd1-skip-link">
        Skip to content
      </a>

      <Nd1SiteNav />

      <main id="nd1-main">
        {/* ---------- Hero ---------- */}
        <section className="nd1-shell nd1-hero">
          <div className="nd1-hero__content">
            <span className="nd1-eyebrow-chip">
              <ShieldCheck className="nd1-icon" aria-hidden="true" />
              Verified hiring for real people
            </span>

            <h1 className="nd1-hero__title">
              <span className="nd1-ink-green">genuine jobs.</span>
              <span className="nd1-ink-orange">real people.</span>
            </h1>

            <div className="nd1-hero__lede">
              <p>
                The job market is drowning in fake profiles, AI generated applications, and
                misleading job ads.
              </p>
              <p>
                JOBUWIN helps genuine applicants and genuine hiring managers find each other through
                more authentic communication.
              </p>
            </div>

            <div className="nd1-hero__actions">
              <Link to="/signup?type=applicant" className="nd1-btn nd1-btn--primary">
                Find genuine jobs
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </Link>
              <Link to="/signup?type=hiring-manager" className="nd1-btn nd1-btn--accent">
                Start hiring
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </Link>
              <button type="button" className="nd1-watch-btn" onClick={() => setVideoOpen(true)}>
                <WatchVideoIcon className="nd1-icon" aria-hidden="true" />
                Watch intro video
              </button>
            </div>

            {/* Trust logo row hidden for now — re-enable when real partner logos are ready. */}
          </div>

          <div className="nd1-hero__compare">
            <div className="nd1-card nd1-compare-card">
              <h4 className="nd1-compare-card__title">Hiring today</h4>
              <div className="nd1-funnel">
                {FUNNEL_ROWS.map((row, index) => {
                  const RowIcon = row.icon;
                  return (
                    <div key={row.label}>
                      {index > 0 ? (
                        <div className="nd1-funnel__sep" aria-hidden="true">
                          <FunnelArrowIcon className="nd1-icon" />
                        </div>
                      ) : null}
                      <div
                        className={`nd1-funnel__row${
                          row.variant ? ` nd1-funnel__row--${row.variant}` : ""
                        }`}
                      >
                        <span className="nd1-funnel__icon">
                          <RowIcon className="nd1-icon" aria-hidden="true" />
                        </span>
                        <span className="nd1-funnel__text">
                          <span className="nd1-funnel__num">{row.num}</span>
                          <span className="nd1-funnel__label">{row.label}</span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="nd1-compare__arrow" aria-hidden="true">
              <ChevronRight className="nd1-icon" />
            </div>

            <div className="nd1-card nd1-with-card">
              <h4 className="nd1-with-card__title">
                With <span className="nd1-win">JOBUWIN</span>
              </h4>
              <img
                className="nd1-with-card__photo"
                src="/images/with-jobuwin-applicant.png"
                alt="A verified JOBUWIN applicant"
                width="120"
                height="120"
                loading="lazy"
              />
              <ul className="nd1-check-list">
                {WITH_JOBUWIN.map((item) => (
                  <li key={item} className="nd1-check-list__item">
                    <GreenTickIcon className="nd1-icon" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ---------- Why we built JOBUWIN ---------- */}
        <section className="nd1-shell nd1-section">
          <div className="nd1-card nd1-why">
            <div className="nd1-why__top">
              <div className="nd1-why__art" aria-hidden="true">
                <img
                  className="nd1-why__art-img"
                  src="/images/why-jobuwin-illustration.png"
                  alt="A verified applicant and hiring manager connecting"
                  loading="lazy"
                />
              </div>

              <div className="nd1-why__text">
                <h2 className="nd1-why__heading">Why we built JOBUWIN</h2>
                <p className="nd1-why__p">The job market is drowning in noise.</p>
                <p className="nd1-why__p">
                  Fake profiles. AI generated applications. Misleading job ads.
                </p>
                <p className="nd1-why__p">
                  Applicants feel ignored.
                  <br />
                  Hiring managers feel overwhelmed.
                </p>
                <p className="nd1-why__highlight">
                  We built JOBUWIN to help genuine people find each other again.
                </p>
              </div>
            </div>

            <div className="nd1-why__cards">
              <Link to="/blog/hiring-is-broken" className="nd1-promo-card nd1-promo-card--orange">
                <span className="nd1-promo-card__icon">
                  <Briefcase className="nd1-icon" aria-hidden="true" />
                </span>
                <span className="nd1-promo-card__body">
                  <span className="nd1-promo-card__title nd1-h4">Learn about hiring on JOBUWIN</span>
                  <span className="nd1-promo-card__copy">
                    Discover how we help hiring managers reduce noise and create better
                    conversations.
                  </span>
                </span>
                <ArrowRight className="nd1-icon nd1-promo-card__arrow" aria-hidden="true" />
              </Link>

              <Link
                to="/blog/applying-for-jobs-is-broken"
                className="nd1-promo-card nd1-promo-card--green"
              >
                <span className="nd1-promo-card__icon">
                  <UserRound className="nd1-icon" aria-hidden="true" />
                </span>
                <span className="nd1-promo-card__body">
                  <span className="nd1-promo-card__title nd1-h4">Learn about applying on JOBUWIN</span>
                  <span className="nd1-promo-card__copy">
                    Discover how you can stand out through genuine communication, not just a polished
                    CV.
                  </span>
                </span>
                <ArrowRight className="nd1-icon nd1-promo-card__arrow" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* ---------- How it works ---------- */}
        <section id="how-it-works" className="nd1-shell nd1-section">
          <div className="nd1-how__head">
            <div className="nd1-eyebrow">How it works</div>
            <h2 className="nd1-section-title nd1-how__title">A better way to hire and get hired.</h2>
          </div>
          <div className="nd1-steps">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div key={step.title} className="nd1-step">
                  <span className="nd1-step__icon">
                    <StepIcon className="nd1-icon" aria-hidden="true" />
                  </span>
                  <div className="nd1-step__body">
                    <div className="nd1-step__head">
                      <span className="nd1-step__num">{index + 1}</span>
                      <h3 className="nd1-step__title">{step.title}</h3>
                    </div>
                    <p className="nd1-step__desc">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ---------- Real job talk ---------- */}
        <section className="nd1-shell nd1-section">
          <div className="nd1-insights">
            <div className="nd1-insights__head">
              <div>
                <div className="nd1-eyebrow">Real job talk</div>
                <h3 className="nd1-section-title nd1-insights__title">
                  Insights and stories about the future of hiring.
                </h3>
              </div>
              <Link to="/blog" className="nd1-explore-link">
                Explore all articles
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </Link>
            </div>

            <div className="nd1-insight-grid">
              {INSIGHTS.map((post) => (
                <Nd1InsightCard key={post.href} post={post} />
              ))}
            </div>
          </div>
        </section>

        {/* ---------- Trust band ---------- */}
        <section className="nd1-shell nd1-section nd1-section--tight">
          <div className="nd1-trust">
            <TrustShieldIcon className="nd1-trust__icon" aria-hidden="true" />
            <div>
              <h2 className="nd1-trust__title">A trusted place for genuine hiring.</h2>
              <p className="nd1-trust__copy">
                Designed to help hiring managers and applicants connect without fake jobs, fake
                applicants, spam, or ghost listings.
              </p>
            </div>
            <ul className="nd1-trust__list">
              {TRUST_POINTS.map((point) => (
                <li key={point}>
                  <GreenTickIcon className="nd1-icon" aria-hidden="true" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <Nd1SiteFooter />

      <YoutubeVideoLightbox
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        videoId={JOBUWIN_INTRO_VIDEO_ID}
        title="Introducing JOBUWIN"
      />
    </div>
  );
}
